/**
 * @Description: Sphere 用户管理相关接口
 * @Doc: SphereUser-API.md
 * 基础路径：/sphere-admin/api/v1/sphere/users
 * 鉴权：请求头携带 Authorization（Bearer），由 clientRequest 统一注入
 */
import request from './clientRequest'

const USER_BASE = '/sphere-admin/api/v1/sphere/users'

/**
 * 1. 新增用户
 * @param {Object} data 用户参数
 * @param {String} data.username 用户账号（必填）
 * @param {String} [data.displayName] 用户姓名
 * @param {String} [data.userType] 用户类型
 * @param {String} [data.digitalHuman] 数字人
 * @param {Number} [data.dialogPermission] 对话权限：0-未开通，1-已开通
 * @param {Number} [data.digitalHumanPermission] 数字人权限：0-未开通，1-已开通
 * @param {String} [data.companyName] 公司名称
 * @param {Number} [data.totalPoints] 总积分
 * @param {Number} [data.usedPoints] 已消耗积分
 * @param {Number} data.iamUserId iam 用户 id（必填）
 * @returns {Promise} data 为完整用户对象
 */
export function addSphereUser(data) {
  return request({
    url: `${USER_BASE}/add`,
    method: 'post',
    data
  })
}

/**
 * 2. 查询用户详情
 * @param {Number|String} id 用户主键 ID
 * @returns {Promise} data 为完整用户对象
 */
export function getSphereUser(id) {
  return request({
    url: `${USER_BASE}/${id}`,
    method: 'get'
  })
}

/**
 * 3. 分页查询用户
 * @param {Object} params 查询参数
 * @param {Number} [params.page] 页码，默认 1
 * @param {Number} [params.size] 每页大小，默认 10
 * @param {String} [params.username] 用户账号（模糊）
 * @param {String} [params.displayName] 用户姓名（模糊）
 * @param {String} [params.userType] 用户类型（精确）
 * @param {String} [params.digitalHuman] 数字人（模糊）
 * @param {Number} [params.dialogPermission] 对话权限：0-未开通，1-已开通
 * @param {Number} [params.digitalHumanPermission] 数字人权限：0-未开通，1-已开通
 * @param {String} [params.companyName] 公司名称（模糊）
 * @param {Number} [params.iamUserId] iam 用户 id（精确）
 * @returns {Promise} data 为分页结构 { records, total }
 */
export function pageSphereUsers(params) {
  return request({
    url: `${USER_BASE}/page`,
    method: 'get',
    params
  })
}

/**
 * 用户详情（用户管理-详情）
 * @param {String} iamUserId
 * @returns {Promise} data 见 SphereUserDetailVO
 */
export function getSphereUserDetail(iamUserId) {
  return request({
    url: `${USER_BASE}/detail`,
    method: 'get',
    params: { iamUserId }
  })
}

/**
 * 客户详情-检索租户成员（Eiip，姓名/账号）
 * @param {Number|String} tenantId sphere_tenant.tenantId
 * @param {String} [keyword] 姓名/账号
 * @param {Number} [pageNum]
 * @param {Number} [pageSize]
 * @returns {Promise} data 为 { rows, total }
 */
export function pageTenantMemberOptions(params) {
  return request({
    url: `${USER_BASE}/tenant/members/page`,
    method: 'get',
    params
  })
}

/**
 * 租户席位可加校验（添加成员前）
 * @param {Number|String} tenantId
 * @returns {Promise} data 为 { userNum, occupiedSeat, canAdd, message }
 */
export function checkSeatAvailable(tenantId) {
  return request({
    url: `${USER_BASE}/seat/available`,
    method: 'get',
    params: { tenantId }
  })
}

/**
 * 客户详情-添加所选成员到 sphere_user
 * @param {Object} data { tenantId, members: [{userId,username,displayName,mobile,roleDesc}] }
 * @returns {Promise}
 */
export function addTenantMembers(data) {
  return request({
    url: `${USER_BASE}/tenant/members/add`,
    method: 'post',
    data
  })
}

/**
 * 5. 更新用户（稀疏更新）
 * @param {Number|String} id 用户主键 ID
 * @param {Object} data 待更新字段（可选，仅更新传入字段）
 * @returns {Promise} data 为更新后的用户对象
 */
export function updateSphereUser(id, data) {
  return request({
    url: `${USER_BASE}/update/${id}`,
    method: 'post',
    data
  })
}

/**
 * 6. 删除用户（逻辑删除）
 * @param {Number|String} id 用户主键 ID
 * @returns {Promise} data 为 null
 */
export function deleteSphereUser(id) {
  return request({
    url: `${USER_BASE}/delete/${id}`,
    method: 'post'
  })
}

/**
 * 7. 校验当前用户/租户是否还有可用席位（订阅付费数字人前调用）
 * @returns {Promise} data 为席位校验结果：
 *   userNum: Number   套餐用户数上限
 *   rank:    Number   当前用户在租户内按 sphere_user.createTime 的排名
 *   valid:   String   是否有可用席位（如 'true'/'false'）
 *   message: String   校验未通过时的提示信息
 */
export function validateSphereUserSeat() {
  return request({
    url: `${USER_BASE}/seat/validate`,
    method: 'get'
  })
}

/**
 * 解析席位校验接口返回：valid 为字符串 true/false（兼容布尔/数字），用于判断当前是否有可用席位
 * 订阅数字人前、数字人会话发送问题前共用
 * @param {Object|string|boolean} data 接口返回的 data
 * @returns {boolean} true 表示有可用席位
 */
export function isSphereSeatValid(data) {
  const PASS = ['true', '1', 'yes', 'y', 'ok', 'pass', 'success', 'valid', '可用', '通过', '有效']
  if (typeof data === 'boolean') return data
  if (typeof data === 'number') return data > 0
  if (typeof data === 'string') {
    return PASS.includes(data.trim().toLowerCase())
  }
  if (!data || typeof data !== 'object') return false
  const v = data.valid
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v > 0
  if (typeof v === 'string') {
    return PASS.includes(v.trim().toLowerCase())
  }
  return false
}
